import React from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import Card from '../../../../../ui/canon/Card';
import Button from '../../../../../ui/canon/Button';
import ProposalSubmissionForm from './ProposalSubmissionForm';

// Spinner SVG used in loading states
const LoadingSpinner = () => (
  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
  </svg>
);

// Muted spinner used in "awaiting" states
const MutedSpinner = ({ text }) => (
  <div className="flex items-center justify-center text-sm text-succession-text-muted">
    <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
    </svg>
    {text}
  </div>
);

class BuyerWorkflowControls extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      showingProposalForm: false
    };
  }

  // Helper function to get workflow state display name
  getWorkflowStateDisplay = (state) => {
    const { intl } = this.props;
    const stateMap = {
      'matching': intl.formatMessage({ id: 'succession.buyer_room.workflow_controls.state_matching' }),
      'invited': intl.formatMessage({ id: 'succession.buyer_room.workflow_controls.state_invited' }),
      'reviewing_proposal': intl.formatMessage({ id: 'succession.buyer_room.workflow_controls.state_reviewing_proposal' }),
      'shortlisted': intl.formatMessage({ id: 'succession.buyer_room.workflow_controls.state_shortlisted' }),
      'out_for_receiving_signature': intl.formatMessage({ id: 'succession.buyer_room.workflow_controls.state_out_for_receiving_signature' }),
      'awaiting_disclosing_signature': intl.formatMessage({ id: 'succession.buyer_room.workflow_controls.state_awaiting_disclosing_signature' }),
      'nda_completed': intl.formatMessage({ id: 'succession.buyer_room.workflow_controls.state_nda_completed' }),
      'negotiating': intl.formatMessage({ id: 'succession.buyer_room.workflow_controls.state_negotiating' }),
      'declined': intl.formatMessage({ id: 'succession.buyer_room.workflow_controls.state_declined' }),
      'declined_by_buyer': intl.formatMessage({ id: 'succession.buyer_room.workflow_controls.state_declined_by_buyer' }),
      'archived': intl.formatMessage({ id: 'succession.buyer_room.workflow_controls.state_archived' }),
    };

    return stateMap[state] || state;
  };

  getCurrentStepNumber = (state) => {
    const stepMap = {
      invited: 1,
      reviewing_proposal: 2,
      shortlisted: 3,
      out_for_receiving_signature: 4,
      awaiting_disclosing_signature: 4,
      nda_completed: 4,
      negotiating: 5
    };

    return stepMap[state] || 1;
  };

  handleShowProposalForm = () => {
    this.setState({ showingProposalForm: true });
  };

  handleCancelProposal = () => {
    this.setState({ showingProposalForm: false });
  };

  renderStatusBadge = () => {
    const { dealInterest, intl } = this.props;
    const workflowState = dealInterest.workflow_state;
    const stepNumber = this.getCurrentStepNumber(workflowState);
    const currentStepLabel = this.getWorkflowStateDisplay(workflowState);

    return (
      <div className="flex items-center gap-3 mb-4">
        <div
          className="w-10 h-10 rounded-full text-white flex items-center justify-center font-semibold flex-shrink-0"
          style={{ backgroundColor: 'var(--succession-primary, #5489B6)' }}
        >
          {stepNumber}
        </div>
        <div className="flex-1">
          <div className="font-medium text-[var(--succession-text-primary)]">
            {currentStepLabel}
          </div>
          <div className="text-sm text-[var(--succession-text-secondary)]">
            {intl.formatMessage({ id: 'succession.buyer_room.workflow_controls.current_stage_label' })}
          </div>
        </div>
      </div>
    );
  };

  renderWorkflowContent = () => {
    const { dealInterest, loading, onSubmitProposal, onDeclineInvitation, onSignNDA, onViewDocuments, onScheduleMeeting, deal, demoSecretMenuEnabled, intl } = this.props;
    const { showingProposalForm } = this.state;
    const workflowState = dealInterest.workflow_state;

    // Show proposal form if user clicked to submit proposal
    if (showingProposalForm && workflowState === 'invited') {
      return (
        <ProposalSubmissionForm
          dealInterest={dealInterest}
          deal={deal}
          onSubmitProposal={(input) => {
            onSubmitProposal(input);
            this.setState({ showingProposalForm: false });
          }}
          onCancel={this.handleCancelProposal}
          loading={loading}
          demoSecretMenuEnabled={demoSecretMenuEnabled}
        />
      );
    }

    switch (workflowState) {
      case 'invited':
        return (
          <div className="space-y-6">
            {this.renderStatusBadge()}

            <div className="bg-[var(--succession-bg-secondary)] border border-[var(--succession-border)] rounded-lg p-4">
              <h3 className="text-lg font-semibold text-[var(--succession-text-primary)] mb-2">
                {intl.formatMessage({ id: 'succession.buyer_room.workflow_controls.invited_title' })}
              </h3>
              <p className="text-[var(--succession-text-secondary)] text-sm mb-4">
                {intl.formatMessage({ id: 'succession.buyer_room.workflow_controls.invited_description' })}
              </p>
            </div>

            <div className="space-y-3">
              <Button
                onClick={this.handleShowProposalForm}
                disabled={loading}
                variant="primary"
                size="md"
                className="w-full"
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <LoadingSpinner />
                    {intl.formatMessage({ id: 'succession.buyer_room.workflow_controls.processing' })}
                  </span>
                ) : (
                  intl.formatMessage({ id: 'succession.buyer_room.workflow_controls.submit_eoi_button' })
                )}
              </Button>

              <Button
                onClick={onDeclineInvitation}
                disabled={loading}
                variant="outline"
                size="md"
                className="w-full"
              >
                {loading
                  ? intl.formatMessage({ id: 'succession.buyer_room.workflow_controls.processing' })
                  : intl.formatMessage({ id: 'succession.buyer_room.workflow_controls.decline_invitation_button' })}
              </Button>
            </div>

            {dealInterest.deadline_passed && (
              <div className="bg-[var(--succession-bg-secondary)] border border-[var(--succession-border)] rounded-lg p-4 mt-4">
                <p className="text-red-700 text-sm font-medium">
                  ⚠️ {intl.formatMessage({ id: 'succession.buyer_room.workflow_controls.deadline_passed' })}
                </p>
              </div>
            )}
          </div>
        );

      case 'reviewing_proposal':
        return (
          <div className="space-y-6">
            {this.renderStatusBadge()}

            <div className="bg-[var(--succession-bg-secondary)] border border-[var(--succession-border)] rounded-lg p-4">
              <h3 className="text-lg font-semibold text-[var(--succession-text-primary)] mb-2">
                {intl.formatMessage({ id: 'succession.buyer_room.workflow_controls.reviewing_proposal_title' })}
              </h3>
              <p className="text-[var(--succession-text-secondary)] text-sm">
                {intl.formatMessage({ id: 'succession.buyer_room.workflow_controls.reviewing_proposal_description' })}
              </p>
            </div>

            {dealInterest.proposal_letter && (
              <div className="bg-[var(--succession-bg-secondary)] border border-[var(--succession-border)] rounded-lg p-4">
                <h4 className="font-medium text-[var(--succession-text-primary)] mb-2">
                  {intl.formatMessage({ id: 'succession.buyer_room.workflow_controls.your_proposal_letter' })}
                </h4>
                <div className="bg-[var(--succession-bg-primary)] p-3 rounded border text-sm text-[var(--succession-text-secondary)] max-h-32 overflow-y-auto">
                  {dealInterest.proposal_letter}
                </div>
              </div>
            )}

            {/* Discovery Questions and Answers */}
            {(dealInterest.a1 || dealInterest.a2 || dealInterest.a3 || dealInterest.a4 || dealInterest.a5) && (
              <div className="bg-[var(--succession-bg-secondary)] border border-[var(--succession-border)] rounded-lg p-4 mt-4">
                <h4 className="font-medium text-[var(--succession-text-primary)] mb-3">
                  {intl.formatMessage({ id: 'succession.buyer_room.workflow_controls.discovery_questions_title' })}
                </h4>
                <div className="space-y-3">
                  {[1, 2, 3, 4, 5].map((num) => {
                    const question = deal[`question_${num}`];
                    const answer = dealInterest[`a${num}`];
                    if (question && answer) {
                      return (
                        <div key={num} className="bg-[var(--succession-bg-primary)] p-3 rounded border">
                          <h5 className="text-sm font-bold text-[var(--succession-text-primary)] mb-1">{question}</h5>
                          <p className="text-sm text-[var(--succession-text-secondary)]">{answer}</p>
                        </div>
                      );
                    }
                    return null;
                  })}
                </div>
              </div>
            )}

            <div className="flex items-center justify-center text-sm text-[var(--succession-text-muted)]">
              <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {intl.formatMessage({ id: 'succession.buyer_room.workflow_controls.awaiting_seller_response' })}
            </div>
          </div>
        );

      case 'shortlisted':
        return (
          <div className="space-y-6">
            {this.renderStatusBadge()}

            <div className="bg-[var(--succession-bg-secondary)] border border-[var(--succession-border)] rounded-lg p-4">
              <h3 className="text-lg font-semibold text-[var(--succession-text-primary)] mb-2">
                🎉 {intl.formatMessage({ id: 'succession.buyer_room.workflow_controls.shortlisted_title' })}
              </h3>
              <p className="text-[var(--succession-text-secondary)] text-sm">
                {intl.formatMessage({ id: 'succession.buyer_room.workflow_controls.shortlisted_description' })}
              </p>
            </div>

            <div className="bg-[var(--succession-bg-secondary)] border border-[var(--succession-border)] rounded-lg p-4">
              <h4 className="font-medium text-[var(--succession-text-primary)] mb-2">
                {intl.formatMessage({ id: 'succession.buyer_room.workflow_controls.whats_next_title' })}
              </h4>
              <ul className="text-[var(--succession-text-secondary)] text-sm space-y-1">
                <li>• {intl.formatMessage({ id: 'succession.buyer_room.workflow_controls.shortlisted_step1' })}</li>
                <li>• {intl.formatMessage({ id: 'succession.buyer_room.workflow_controls.shortlisted_step2' })}</li>
                <li>• {intl.formatMessage({ id: 'succession.buyer_room.workflow_controls.shortlisted_step3' })}</li>
                <li>• {intl.formatMessage({ id: 'succession.buyer_room.workflow_controls.shortlisted_step4' })}</li>
              </ul>
            </div>

            <div className="flex items-center justify-center text-sm text-[var(--succession-text-muted)]">
              <svg className="animate-pulse h-4 w-4 mr-2 text-purple-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              {intl.formatMessage({ id: 'succession.buyer_room.workflow_controls.waiting_nda_from_seller' })}
            </div>
          </div>
        );

      case 'out_for_receiving_signature':
        return (
          <div className="space-y-6">
            {this.renderStatusBadge()}

            <div className="bg-[var(--succession-bg-secondary)] border border-[var(--succession-border)] rounded-lg p-4">
              <h3 className="text-lg font-semibold text-[var(--succession-text-primary)] mb-2">
                {intl.formatMessage({ id: 'succession.buyer_room.workflow_controls.nda_ready_title' })}
              </h3>
              <p className="text-[var(--succession-text-secondary)] text-sm">
                {intl.formatMessage({ id: 'succession.buyer_room.workflow_controls.nda_ready_description' })}
              </p>
            </div>

            <Button
              onClick={onSignNDA}
              disabled={loading}
              variant="primary"
              size="lg"
              className="w-full bg-orange-600 hover:bg-orange-700 disabled:bg-orange-300 animate-pulse hover:animate-none"
            >
              {loading ? (
                <span className="flex items-center">
                  <LoadingSpinner />
                  {intl.formatMessage({ id: 'succession.buyer_room.workflow_controls.loading' })}
                </span>
              ) : (
                <>
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  {intl.formatMessage({ id: 'succession.buyer_room.workflow_controls.review_sign_nda_button' })}
                </>
              )}
            </Button>

            <div className="bg-[var(--succession-bg-secondary)] border border-[var(--succession-border)] rounded-lg p-4">
              <h4 className="font-medium text-[var(--succession-text-primary)] mb-2">
                {intl.formatMessage({ id: 'succession.buyer_room.workflow_controls.about_ndas_title' })}
              </h4>
              <p className="text-[var(--succession-text-secondary)] text-sm">
                {intl.formatMessage({ id: 'succession.buyer_room.workflow_controls.about_ndas_description' })}
              </p>
            </div>
          </div>
        );

      case 'awaiting_disclosing_signature':
        return (
          <div className="space-y-6">
            {this.renderStatusBadge()}

            <div className="bg-[var(--succession-bg-secondary)] border border-[var(--succession-border)] rounded-lg p-4">
              <h3 className="text-lg font-semibold text-[var(--succession-text-primary)] mb-2">
                {intl.formatMessage({ id: 'succession.buyer_room.workflow_controls.awaiting_sellers_signature_title' })}
              </h3>
              <p className="text-[var(--succession-text-secondary)] text-sm">
                {intl.formatMessage({ id: 'succession.buyer_room.workflow_controls.awaiting_sellers_signature_description' })}
              </p>
            </div>

            <div className="flex items-center justify-center text-sm text-[var(--succession-text-muted)]">
              <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {intl.formatMessage({ id: 'succession.buyer_room.workflow_controls.waiting_sellers_signature' })}
            </div>
          </div>
        );

      case 'negotiating':
        return (
          <div className="space-y-6">
            {this.renderStatusBadge()}

            <div className="bg-[var(--succession-bg-secondary)] border border-[var(--succession-border)] rounded-lg p-4">
              <h3 className="text-lg font-semibold text-[var(--succession-text-primary)] mb-2">
                🚀 {intl.formatMessage({ id: 'succession.buyer_room.workflow_controls.ready_to_negotiate_title' })}
              </h3>
              <p className="text-[var(--succession-text-secondary)] text-sm">
                {intl.formatMessage({ id: 'succession.buyer_room.workflow_controls.ready_to_negotiate_description' })}
              </p>
            </div>

            <div className="grid grid-cols-1 gap-3">
              <Button
                as="a"
                href="#message-seller"
                variant="primary"
                size="lg"
                className="w-full"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                {intl.formatMessage({ id: 'succession.buyer_room.workflow_controls.message_seller_button' })}
              </Button>
            </div>

            <div className="grid grid-cols-1 gap-3">
              <Button
                onClick={onScheduleMeeting}
                variant="primary"
                size="lg"
                className="w-full"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                {intl.formatMessage({ id: 'succession.buyer_room.workflow_controls.schedule_meeting_button' })}
              </Button>
            </div>

            <div className="bg-[var(--succession-bg-secondary)] border border-[var(--succession-border)] rounded-lg p-4">
              <h4 className="font-medium text-[var(--succession-text-primary)] mb-2">
                {intl.formatMessage({ id: 'succession.buyer_room.workflow_controls.your_signed_nda_title' })}
              </h4>
              <p className="text-[var(--succession-text-secondary)] text-sm mb-3">
                {intl.formatMessage({ id: 'succession.buyer_room.workflow_controls.your_signed_nda_description' })}
              </p>
              <a
                href={`/deal/${deal.deal_name}/open_file/${dealInterest.id}`}
                className="text-sm font-medium text-succession-primary hover:text-succession-primary-hover underline"
              >
                {intl.formatMessage({ id: 'succession.buyer_room.workflow_controls.download_executed_nda' })} →
              </a>
            </div>
          </div>
        );

      case 'declined_by_buyer':
        return (
          <div className="space-y-6">
            {this.renderStatusBadge()}

            <div className="bg-[var(--succession-bg-secondary)] border border-[var(--succession-border)] rounded-lg p-4">
              <h3 className="text-lg font-semibold text-[var(--succession-text-primary)] mb-2">
                {intl.formatMessage({ id: 'succession.buyer_room.workflow_controls.invitation_declined_title' })}
              </h3>
              <p className="text-[var(--succession-text-secondary)] text-sm">
                {intl.formatMessage({ id: 'succession.buyer_room.workflow_controls.invitation_declined_description' })}
              </p>
              {dealInterest.buyer_declined_date && (
                <p className="text-[var(--succession-text-muted)] text-xs mt-2">
                  {intl.formatMessage(
                    { id: 'succession.buyer_room.workflow_controls.declined_on' },
                    { date: new Date(dealInterest.buyer_declined_date).toLocaleDateString() }
                  )}
                </p>
              )}
            </div>
          </div>
        );

      default:
        return (
          <div className="space-y-6">
            {this.renderStatusBadge()}

            <div className="bg-[var(--succession-bg-secondary)] border border-[var(--succession-border)] rounded-lg p-4">
              <h3 className="text-lg font-semibold text-[var(--succession-text-primary)] mb-2">
                {intl.formatMessage({ id: 'succession.buyer_room.workflow_controls.status_update_title' })}
              </h3>
              <p className="text-[var(--succession-text-secondary)] text-sm">
                {intl.formatMessage(
                  { id: 'succession.buyer_room.workflow_controls.status_update_description' },
                  { status: this.getWorkflowStateDisplay(workflowState) }
                )}
              </p>
            </div>
          </div>
        );
    }
  };

  render() {
    const { intl } = this.props;
    return (
      <Card size="lg" className="sticky top-6">
        <h3 className="text-lg font-semibold text-[var(--succession-text-primary)] mb-6">
          {intl.formatMessage({ id: 'succession.buyer_room.workflow_controls.next_steps_title' })}
        </h3>
        {this.renderWorkflowContent()}
      </Card>
    );
  }
}

BuyerWorkflowControls.propTypes = {
  deal: PropTypes.object.isRequired,
  dealInterest: PropTypes.object.isRequired,
  company: PropTypes.object.isRequired,
  currentUser: PropTypes.object.isRequired,
  loading: PropTypes.bool,
  onSubmitProposal: PropTypes.func.isRequired,
  onDeclineInvitation: PropTypes.func.isRequired,
  onSignNDA: PropTypes.func.isRequired,
  onViewDocuments: PropTypes.func.isRequired,
  onScheduleMeeting: PropTypes.func.isRequired,
  onStatusUpdate: PropTypes.func,
  demoSecretMenuEnabled: PropTypes.bool,
  intl: PropTypes.object.isRequired,
};

BuyerWorkflowControls.defaultProps = {
  loading: false,
  demoSecretMenuEnabled: false
};

export default injectIntl(BuyerWorkflowControls);
